// src/lib/api.js — Centralized API client for CTEMS
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ctems_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 — redirect to login on expired/invalid session (except during login requests)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('ctems_token');
      localStorage.removeItem('ctems_user');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ─── Auth ────────────────────────────────────────────────────────────────────
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

// ─── Users ───────────────────────────────────────────────────────────────────
export const usersApi = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  changePassword: (id, data) => api.put(`/users/${id}/password`, data),
  subordinates: () => api.get('/users/me/subordinates'),
};

// ─── Travel Requests ─────────────────────────────────────────────────────────
export const travelRequestsApi = {
  list: (params) => api.get('/travel-requests', { params }),
  get: (id) => api.get(`/travel-requests/${id}`),
  create: (data) => api.post('/travel-requests', data),
  update: (id, data) => api.put(`/travel-requests/${id}`, data),
  delete: (id) => api.delete(`/travel-requests/${id}`),
  submit: (id) => api.post(`/travel-requests/${id}/submit`),
  approve: (id, data) => api.post(`/travel-requests/${id}/approve`, data),
  reject: (id, data) => api.post(`/travel-requests/${id}/reject`, data),
  uploadItinerary: (id, data) => api.post(`/travel-requests/${id}/itinerary`, data),
};

// ─── Expense Claims ───────────────────────────────────────────────────────────
export const expenseClaimsApi = {
  list: (params) => api.get('/expense-claims', { params }),
  get: (id) => api.get(`/expense-claims/${id}`),
  create: (data) => api.post('/expense-claims', data),
  submit: (id) => api.post(`/expense-claims/${id}/submit`),
  approve: (id, data) => api.post(`/expense-claims/${id}/approve`, data),
  reject: (id, data) => api.post(`/expense-claims/${id}/reject`, data),
  settle: (id, data) => api.post(`/expense-claims/${id}/settle`, data),
  checkPolicy: (items) => api.post('/expense-claims/policy/check', { items }),
};

// ─── Policies ────────────────────────────────────────────────────────────────
export const policiesApi = {
  list: () => api.get('/policies'),
  myPolicy: () => api.get('/policies/my'),
  get: (id) => api.get(`/policies/${id}`),
  create: (data) => api.post('/policies', data),
  update: (id, data) => api.put(`/policies/${id}`, data),
  delete: (id) => api.delete(`/policies/${id}`),
};

// ─── Cash Advances ────────────────────────────────────────────────────────────
export const cashAdvancesApi = {
  list: (params) => api.get('/cash-advances', { params }),
  get: (id) => api.get(`/cash-advances/${id}`),
  aging: () => api.get('/cash-advances/aging'),
  create: (data) => api.post('/cash-advances', data),
  approve: (id, data) => api.post(`/cash-advances/${id}/approve`, data),
  reject: (id, data) => api.post(`/cash-advances/${id}/reject`, data),
  reconcile: (id, data) => api.post(`/cash-advances/${id}/reconcile`, data),
};

// ─── Vendors ─────────────────────────────────────────────────────────────────
export const vendorsApi = {
  list: (params) => api.get('/vendors', { params }),
  get: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
};

// ─── Notifications ────────────────────────────────────────────────────────────
export const notificationsApi = {
  list: (params) => api.get('/notifications', { params }),
  count: () => api.get('/notifications/count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// ─── Reports ─────────────────────────────────────────────────────────────────
export const reportsApi = {
  dashboard: () => api.get('/reports/dashboard'),
  spending: (params) => api.get('/reports/spending', { params }),
  violations: () => api.get('/reports/violations'),
  travelRequests: (params) => api.get('/reports/travel-requests', { params }),
  advances: () => api.get('/reports/advances'),
};

// ─── Upload API ──────────────────────────────────────────────────────────────
export const uploadApi = {
  uploadFile: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result;
          const { data } = await api.post('/upload', {
            filename: file.name,
            mime_type: file.type,
            filedata: base64Data,
          });
          resolve(data);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  },
};

// ─── Cities ──────────────────────────────────────────────────────────────────
export const citiesApi = {
  list: (params) => api.get('/cities', { params }),
  get: (id) => api.get(`/cities/${id}`),
  create: (data) => api.post('/cities', data),
};

// ─── Demo Requests (landing page) ─────────────────────────────────────────────
export const demoApi = {
  create: (data) => api.post('/demo-request', data),
  list: () => api.get('/demo-requests'),
};

export default api;
